import { Box, Divider, Flex, Stack, Text } from '@chakra-ui/react';
import { isSubscription } from 'components/cart/utils/typeInference';
import { StickyCta } from 'components/heros/AnchorCtas/StickyCta';
import { FormatPrice } from 'components/localisation/FormatPrice';
import type { PromoCode } from 'types/cart/PromoCode';
import type { Variant } from 'types/product/Variant';
import type { VariantOrSubscription } from 'types/product/VariantOrSubscription';

import { ProductAddToCartButton } from '../ProductAddToCartButton';
import { ProductBuyOnAmazonButton } from '../ProductBuyOnAmazonButton';
import type { ProductBlockOnlyProps } from '../ProductConfiguratorRender';
import { ProductOutOfStockButton } from '../ProductOutOfStockButton';

export interface ProductCtasProps extends Omit<
  ProductBlockOnlyProps,
  'showSavingsAsAmount' | 'selectedVariantsIds'
> {
  variant: VariantOrSubscription;
  otpVariant: Variant;
  isPreorder?: boolean;
  showOTPLink?: boolean;
  promoCodes?: PromoCode[];
  isSupplementPicker?: boolean;
  showStickyCta?: boolean;
}

export function ProductCtas({
  isPreorder,
  variant,
  otpVariant,
  ctaType,
  promoCodes,
  amazonButton,
  asin,
  purchaseType,
  showOTPLink,
  isDiscountHidden,
  isSupplementPicker,
  showStickyCta,
}: ProductCtasProps) {
  const hasDiscount =
    otpVariant.compareAtPrice &&
    otpVariant.compareAtPrice.amount - otpVariant.price.amount > 0;

  return (
    <Stack
      data-testid="product_ctas"
      className="product_ctas"
      alignItems="stretch"
      rowGap="sm"
    >
      <Stack alignItems="stretch" rowGap={0}>
        {!variant.availableForSale && (
          <ProductOutOfStockButton productVariantId={otpVariant.id} />
        )}
        {variant.sku && variant.availableForSale && (
          <ProductAddToCartButton
            merchandiseId={variant.id}
            sellingPlanId={
              isSubscription(variant) ? variant.sellingPlan.id : undefined
            }
            ctaType={ctaType}
            promoCodes={promoCodes}
            isPreorder={isPreorder}
            purchaseType={purchaseType}
            isDiscountHidden={isDiscountHidden}
          >
            {isSupplementPicker && isSubscription(variant)
              ? 'Subscribe & Save'
              : undefined}
          </ProductAddToCartButton>
        )}

        {amazonButton?.hasAmazonButton &&
          otpVariant.availableForSale &&
          asin && (
            <>
              {ctaType !== 'none' && (
                <Flex gap="2xs" alignItems="center" my="xs">
                  <Divider opacity={1} borderColor="blackAlpha.250" />
                  <Text lineHeight={0} size="overline" color="blackAlpha.700">
                    OR
                  </Text>
                  <Divider opacity={1} borderColor="blackAlpha.250" />
                </Flex>
              )}

              <ProductBuyOnAmazonButton
                size="md"
                minWidth="100%"
                asin={asin}
                variant={ctaType === 'none' ? 'primary' : undefined}
                overrideUrl={amazonButton.overrideUrl}
              />
            </>
          )}
      </Stack>

      {showOTPLink && otpVariant?.sku && otpVariant.availableForSale && (
        <Box textAlign="center">
          <ProductAddToCartButton
            merchandiseId={otpVariant.id}
            variant="link"
            promoCodes={promoCodes}
            isDiscountHidden
          >
            One-time purchase ·{' '}
            {!isDiscountHidden && hasDiscount && otpVariant.compareAtPrice ? (
              <>
                <FormatPrice amount={otpVariant.price.amount} />{' '}
                <FormatPrice
                  size="overline"
                  color="blackAlpha.500"
                  hasStrikeThrough
                  amount={otpVariant.compareAtPrice.amount}
                />
              </>
            ) : (
              <FormatPrice amount={otpVariant.price.amount} />
            )}
          </ProductAddToCartButton>
        </Box>
      )}

      {showStickyCta && variant.sku && variant.availableForSale && (
        <StickyCta
          type="scrollToTop"
          isSubscription={isSubscription(variant)}
          showCart={false}
        />
      )}
    </Stack>
  );
}
